import { useMutation } from '@apollo/client';
import type { DeleteTestResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteTestResultsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteTestResultsTRPC } from './useDeleteTestResultsTRPC';

export const useDeleteTestResults = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteTestResultsGraphQL, graphqlState] = useMutation(
    DeleteTestResultsDocument,
    {
      update: (cache) => {
        evictField(cache, 'test_result');
        evictField(cache, 'test_result_aggregate');
        evictField(cache, 'control');
        evictField(cache, 'risk_score');
      },
    }
  );

  const trpcMutation = useDeleteTestResultsTRPC();

  const deleteTestResults = async (variables: {
    ids: string[];
  }): Promise<DeleteTestResultsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteTestResults(variables);
    }

    const result = await deleteTestResultsGraphQL({
      variables: {
        Ids: variables.ids,
      },
    });

    if (!result.data) {
      throw new Error('Failed to delete test results');
    }

    return result.data;
  };

  return {
    deleteTestResults,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
